import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, Shield, TrendingUp, Award, CheckCircle2, Target } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const InstitutionelleInvestoren = () => {
  const [formData, setFormData] = useState({
    email: '',
    assetklassen: [],
    plz_gebiete: '',
    budget_min: '',
    budget_max: '',
    wault_min: '',
    marketing_consent: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/buyer-profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          plz_gebiete: formData.plz_gebiete.split(',').map(s => s.trim()),
          budget_min: parseFloat(formData.budget_min),
          budget_max: parseFloat(formData.budget_max),
          wault_min: parseFloat(formData.wault_min) || null
        })
      });
      
      if (response.ok) {
        toast.success('Suchprofil erfolgreich erstellt! Wir melden uns bei passenden Objekten.');
        setFormData({
          email: '',
          assetklassen: [],
          plz_gebiete: '',
          budget_min: '',
          budget_max: '',
          wault_min: '',
          marketing_consent: false
        });
      } else {
        toast.error('Fehler beim Erstellen des Suchprofils');
      }
    } catch (error) {
      toast.error('Netzwerkfehler. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const assetklassen = [
    { value: 'lebensmittelmarkt', label: 'Lebensmittelmärkte' },
    { value: 'discounter', label: 'Discounter' },
    { value: 'fachmarkt', label: 'Fachmärkte' },
    { value: 'fachmarktzentrum', label: 'Fachmarktzentren' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Handelsimmobilien für institutionelle Investoren – RetailInvest"
        description="Core-Objekte mit stabilen Cashflows für Versicherungen, Pensionsfonds und offene Immobilienfonds. Professionell. Diskret. Großvolumig."
        url="/investoren/institutionell"
      />
      <Navbar />
      <main className="flex-grow bg-gray-50">
        {/* Hero Section */}
        <section
          className="relative min-h-[400px] flex items-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0F2744]/90 to-[#0A1628]/85"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6"></div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Handelsimmobilien für
              <span className="block mt-2">
                <span className="bg-gradient-to-r from-[#D4A853] to-[#F0C870] bg-clip-text text-transparent">
                  Institutionelle Investoren
                </span>
              </span>
            </h1>
            <p className="text-xl text-gray-100 max-w-3xl">
              Core-Objekte mit stabilen Cashflows, langfristigen Mietverträgen und erstklassigen Mietern.
              Professionell vermittelt. Diskret abgewickelt.
            </p>
          </div>
        </section>

        {/* Warum wir Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Warum RetailInvest der richtige Partner für Sie ist
              </h2>
              <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--gray-700)' }}>
                Als spezialisierte Makler verstehen wir die Anforderungen institutioneller Investoren
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <Card className="glass-card p-8 text-center">
                <Shield className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--gold-600)' }} />
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--navy-800)' }}>Diskrete Abwicklung</h3>
                <p style={{ color: 'var(--gray-700)' }}>Off-Market-Deals und vertrauliche Transaktionen mit höchster Professionalität</p>
              </Card>
              <Card className="glass-card p-8 text-center">
                <Building2 className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--gold-600)' }} />
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--navy-800)' }}>Core-Fokus</h3>
                <p style={{ color: 'var(--gray-700)' }}>Ausschließlich stabile Handelsimmobilien mit bonitätsstarken Ankermietern</p>
              </Card>
              <Card className="glass-card p-8 text-center">
                <Award className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--gold-600)' }} />
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--navy-800)' }}>Due Diligence</h3>
                <p style={{ color: 'var(--gray-700)' }}>Umfassende Objektprüfung und Dokumentation nach institutionellen Standards</p>
              </Card>
            </div>
          </div>
        </section>

        {/* Unsere Leistungen */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ color: 'var(--navy-900)' }}>
              Unsere Leistungen für institutionelle Investoren
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                'Portfolio-Aufbau mit strategischem Fokus',
                'ESG-Kriterien und Nachhaltigkeitsbewertung',
                'WAULT-Analyse und Cashflow-Modellierung',
                'Marktrecherche und Standortanalyse',
                'Koordination mit Asset Managern',
                'Langfristige Objektbetreuung nach Ankauf'
              ].map((leistung, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: 'var(--gold-600)' }} />
                  <p className="text-lg" style={{ color: 'var(--gray-700)' }}>{leistung}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Suchprofil Formular */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Target className="h-16 w-16 mx-auto mb-4" style={{ color: 'var(--gold-600)' }} />
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Ihr Suchprofil anlegen
              </h2>
              <p className="text-lg" style={{ color: 'var(--gray-700)' }}>
                Wir informieren Sie proaktiv über passende Core-Objekte
              </p>
            </div>

            <Card className="glass-card p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="email">E-Mail-Adresse *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-12"
                  />
                </div>

                <div>
                  <Label>Interessante Assetklassen *</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {assetklassen.map((ak) => (
                      <div key={ak.value} className="flex items-center gap-2">
                        <Checkbox
                          id={ak.value}
                          checked={formData.assetklassen.includes(ak.value)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, assetklassen: [...formData.assetklassen, ak.value] });
                            } else {
                              setFormData({ ...formData, assetklassen: formData.assetklassen.filter(a => a !== ak.value) });
                            }
                          }}
                        />
                        <Label htmlFor={ak.value} className="cursor-pointer">{ak.label}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="plz_gebiete">PLZ-Gebiete (kommagetrennt) *</Label>
                  <Input
                    id="plz_gebiete"
                    required
                    placeholder="z.B. 50, 51, 80-89"
                    value={formData.plz_gebiete}
                    onChange={(e) => setFormData({ ...formData, plz_gebiete: e.target.value })}
                    className="h-12"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="budget_min">Investitionsvolumen von (€) *</Label>
                    <Input
                      id="budget_min"
                      type="number"
                      required
                      placeholder="5000000"
                      value={formData.budget_min}
                      onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="budget_max">bis (€) *</Label>
                    <Input
                      id="budget_max"
                      type="number"
                      required
                      placeholder="15000000"
                      value={formData.budget_max}
                      onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="wault_min">Mindest-WAULT (Jahre)</Label>
                  <Input
                    id="wault_min"
                    type="number"
                    step="0.1"
                    placeholder="8"
                    value={formData.wault_min}
                    onChange={(e) => setFormData({ ...formData, wault_min: e.target.value })}
                    className="h-12"
                  />
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="marketing_consent"
                    checked={formData.marketing_consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, marketing_consent: checked })}
                  />
                  <Label htmlFor="marketing_consent" className="text-sm cursor-pointer">
                    Ich möchte regelmäßig über passende Objekte und Marktentwicklungen informiert werden.
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 btn-gold-gradient text-white font-semibold text-base"
                >
                  {loading ? 'Wird gesendet...' : 'Suchprofil jetzt anlegen'}
                </Button>
              </form>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default InstitutionelleInvestoren;